/*
 * TGB - delegated AJAX add-to-cart connector.
 *
 * A single document-level submit listener serves every TGB funnel form. Wiring
 * per form would break as soon as a section is reordered or re-rendered by the
 * theme editor, and each funnel section emits its own assets, so this file has
 * to survive being included several times on one page.
 *
 * Only forms carrying the data-tgb-ajax attribute are intercepted. Dawn's own
 * <product-form> elements handle their submit event themselves, so leaving
 * unmarked forms alone is what keeps the two layers from fighting.
 *
 * The request/response contract mirrors assets/product-form.js exactly: the
 * cart element decides which sections the server must re-render, and its
 * renderContents() both paints them and opens the drawer.
 */
(function () {
  window.TGB = window.TGB || {};
  if (window.TGB.cart) return;

  // Language-neutral so no untranslated English leaks onto a localized store.
  // Override per button with data-tgb-loading-label, or globally by setting
  // window.TGB.cartStrings.adding before this file runs.
  var DEFAULT_BUSY_LABEL = '…';

  function rootUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function addUrl() {
    // window.routes is emitted by layout/theme.liquid and already localized.
    if (window.routes && window.routes.cart_add_url) return window.routes.cart_add_url;
    return rootUrl() + 'cart/add.js';
  }

  function cartUrl() {
    if (window.routes && window.routes.cart_url) return window.routes.cart_url;
    return rootUrl() + 'cart';
  }

  // Dawn renders exactly one of these depending on settings.cart_type.
  function findCartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function busyLabel(button) {
    var attr = button.getAttribute('data-tgb-loading-label');
    if (attr) return attr;
    if (window.TGB.cartStrings && window.TGB.cartStrings.adding) return window.TGB.cartStrings.adding;
    return DEFAULT_BUSY_LABEL;
  }

  function setBusy(button) {
    if (!button) return;
    button.setAttribute('data-tgb-cart-label', button.innerHTML);
    button.setAttribute('aria-busy', 'true');
    button.classList.add('tgb-cart-busy');
    button.disabled = true;
    button.textContent = busyLabel(button);
  }

  function clearBusy(button) {
    if (!button) return;
    var previous = button.getAttribute('data-tgb-cart-label');
    if (previous !== null) button.innerHTML = previous;
    button.removeAttribute('data-tgb-cart-label');
    button.removeAttribute('aria-busy');
    button.classList.remove('tgb-cart-busy');
    button.disabled = false;
  }

  function errorNode(form, button) {
    var existing = form.querySelector('.tgb-cart-error');
    if (existing) return existing;
    var node = document.createElement('p');
    node.className = 'tgb-cart-error';
    node.setAttribute('role', 'alert');
    node.hidden = true;
    var anchor = button && button.parentNode ? button : null;
    if (anchor) {
      anchor.parentNode.insertBefore(node, anchor.nextSibling);
    } else {
      form.appendChild(node);
    }
    return node;
  }

  // errors can arrive as a string, an array, or a field-keyed object.
  function normalizeMessage(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.join(' ');
    if (typeof value === 'object') {
      return Object.keys(value)
        .map(function (key) {
          return normalizeMessage(value[key]);
        })
        .join(' ');
    }
    return String(value);
  }

  function showError(form, button, message) {
    var node = errorNode(form, button);
    node.textContent = normalizeMessage(message);
    node.hidden = false;
  }

  function clearError(form) {
    var node = form.querySelector('.tgb-cart-error');
    if (!node) return;
    node.textContent = '';
    node.hidden = true;
  }

  function submitButtonFor(event, form) {
    return event.submitter || form.querySelector('[type="submit"]');
  }

  function onSubmit(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var form = target.closest('form[action*="/cart/add"]');
    if (!form || !form.hasAttribute('data-tgb-ajax')) return;

    event.preventDefault();

    var button = submitButtonFor(event, form);
    if (button && button.disabled) return;

    clearError(form);
    setBusy(button);

    var cart = findCartElement();
    var formData = new FormData(form);

    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cart.getSectionsToRender().map(function (section) {
          return section.id;
        })
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button || document.activeElement);
    }

    fetch(addUrl(), {
      method: 'POST',
      // Content-Type is deliberately omitted so the browser sets the multipart
      // boundary itself; product-form.js deletes it for the same reason.
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (parsed) {
        // Shopify answers 422 with a status field for sold out / limit reached.
        if (parsed && parsed.status) {
          showError(form, button, parsed.description || parsed.message || parsed.errors || '');
          return;
        }

        if (cart && typeof cart.renderContents === 'function') {
          // renderContents paints the returned sections and opens the drawer.
          cart.renderContents(parsed);
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          return;
        }

        // settings.cart_type is "page": no client-side cart element exists.
        window.location = cartUrl();
      })
      .catch(function (error) {
        // Never leave a restored-but-silent button: the shopper has to be told
        // the click did nothing.
        console.error(error);
        showError(form, button, (error && error.message) || error);
      })
      .finally(function () {
        clearBusy(button);
      });
  }

  document.addEventListener('submit', onSubmit);

  // Exported so other TGB scripts can reuse the lookups instead of duplicating
  // the route/cart-element guesswork. The listener itself needs no public API.
  window.TGB.cart = {
    addUrl: addUrl,
    cartUrl: cartUrl,
    findCartElement: findCartElement
  };
})();
